import { useState, useEffect } from "react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { EnvelopeSimple, ArrowRight, ShieldCheck, Copy, ArrowsClockwise, Broom, Broadcast } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

export default function EmailIngest() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [form, setForm] = useState({ from_email: "requester@company.com", subject: "Sample: Cannot connect to Wi-Fi in HQ conference room", body: "Hi team,\n\nWi-Fi in Marina room stops working around 10am daily. This affects all client meetings.\n\nPlease help.\nSam" });
  const [created, setCreated] = useState(null);
  const [saving, setSaving] = useState(false);
  const [ingest, setIngest] = useState(null);
  const [secret, setSecret] = useState("");
  const [savingSecret, setSavingSecret] = useState(false);
  const [mailPoll, setMailPoll] = useState(null);
  const [polling, setPolling] = useState(false);
  const [purging, setPurging] = useState(false);
  const [mbForm, setMbForm] = useState({ mailbox: "", interval_seconds: "" });
  const [savingMb, setSavingMb] = useState(false);
  const [alertCfg, setAlertCfg] = useState(null);
  const [savingAlert, setSavingAlert] = useState(false);
  const [alertTest, setAlertTest] = useState({ subject: "[ALERT: db-cpu-01] High CPU on prod-db-01", body: "Alert ID: db-cpu-01\nCPU sustained > 95% for 5 minutes." });
  const [alertPreview, setAlertPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const webhookUrl = `${API}/email-ingest`;

  const loadMailPoll = () => api.get("/integrations/mail-poll").then(r => {
    setMailPoll(r.data);
    setMbForm({ mailbox: r.data.mailbox || "", interval_seconds: r.data.interval_seconds ?? "" });
  }).catch(() => {});

  const loadAlertCfg = () => api.get("/alert-config").then(r => {
    setAlertCfg({ ...r.data, recovery_keywords: (r.data.recovery_keywords || []).join(", ") });
  }).catch(() => {});

  useEffect(() => {
    if (isAdmin) {
      api.get("/integrations/ingest").then(r => setIngest(r.data)).catch(() => {});
      loadMailPoll();
      loadAlertCfg();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const saveAlertCfg = async () => {
    setSavingAlert(true);
    try {
      const { data } = await api.put("/alert-config", {
        enabled: !!alertCfg.enabled,
        window_hours: alertCfg.window_hours === "" || alertCfg.window_hours == null ? 24 : Math.max(1, Number(alertCfg.window_hours)),
        tag_name: (alertCfg.tag_name || "").trim(),
        id_label: (alertCfg.id_label || "").trim(),
        recovery_keywords: (alertCfg.recovery_keywords || "").split(",").map(k => k.trim()).filter(Boolean),
      });
      setAlertCfg({ ...data, recovery_keywords: (data.recovery_keywords || []).join(", ") });
      toast.success("Alert correlation settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSavingAlert(false); }
  };

  const runAlertPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.post("/alert-config/preview", {
        subject: alertTest.subject,
        body: alertTest.body,
        enabled: !!alertCfg.enabled,
        window_hours: alertCfg.window_hours === "" || alertCfg.window_hours == null ? 24 : Math.max(1, Number(alertCfg.window_hours)),
        tag_name: (alertCfg.tag_name || "").trim(),
        id_label: (alertCfg.id_label || "").trim(),
        recovery_keywords: (alertCfg.recovery_keywords || "").split(",").map(k => k.trim()).filter(Boolean),
      });
      setAlertPreview(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
    finally { setPreviewing(false); }
  };

  const saveMailPoll = async () => {
    setSavingMb(true);
    try {
      await api.put("/integrations/mail-poll", {
        mailbox: mbForm.mailbox.trim(),
        interval_seconds: Number(mbForm.interval_seconds) || 60,
      });
      toast.success("Mailbox settings saved");
      loadMailPoll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSavingMb(false); }
  };

  const togglePolling = async (enabled) => {
    try {
      await api.put("/integrations/mail-poll", { enabled });
      toast.success(enabled ? "Email polling resumed" : "Email polling paused — no new tickets will be created");
      loadMailPoll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update"); }
  };

  const runPoll = async () => {
    setPolling(true);
    try {
      const { data } = await api.post("/integrations/mail-poll/run");
      if (data.status === "ok") toast.success(`Poll complete — ${data.created} new ticket(s)`);
      else toast.error(`Poll failed: ${data.detail || "check Graph permissions"}`);
      loadMailPoll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Poll failed"); }    finally { setPolling(false); }
  };

  const purgeBounces = async () => {
    if (!window.confirm("Delete all bounce / auto-reply / out-of-office loop tickets? This cannot be undone.")) return;
    setPurging(true);
    try {
      const { data } = await api.delete("/incidents/purge-bounces");
      toast.success(`Removed ${data.deleted} loop ticket(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Cleanup failed"); }
    finally { setPurging(false); }
  };

  const genSecret = () => {
    const bytes = new Uint8Array(24);
    crypto.getRandomValues(bytes);
    setSecret(Array.from(bytes).map(b => b.toString(16).padStart(2, "0")).join(""));
  };
  const saveSecret = async () => {
    setSavingSecret(true);
    try {
      const { data } = await api.put("/integrations/ingest", { secret });
      setIngest(data);
      setSecret("");
      toast.success(secret ? "Ingest secret saved" : "Ingest secret cleared (webhook now open)");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSavingSecret(false); }
  };
  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success("Copied"); };

  const send = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/email-ingest", form);
      setCreated(data);
      toast.success(`Ticket ${data.number} created from email`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="p-6" data-testid="email-ingest-view">
      <div className="mb-6">
        <div className="text-[11px] font-mono uppercase tracking-widest text-primary mb-1">Office 365 Inbound Mail (mocked)</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black">Email → Ticket</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Simulate an inbound email to <span className="font-mono text-primary">{mailPoll?.mailbox || "your helpdesk mailbox"}</span>. In production, your mail provider (Power Automate / Microsoft Graph) posts to the webhook below.
        </p>
      </div>

      {isAdmin && (
        <Card className="p-6 border-border rounded-sm mb-6" data-testid="mail-pull-card">
          <div className="flex items-center gap-2 mb-4">
            <EnvelopeSimple size={16} className="text-primary" />
            <span className="text-[11px] font-mono uppercase tracking-widest">Office 365 Email Pull</span>
            <span className={`ml-2 text-[10px] font-mono px-2 py-0.5 border rounded-sm ${mailPoll?.configured ? "border-emerald-500/50 text-emerald-700 dark:text-emerald-400" : "border-amber-500/50 text-amber-700 dark:text-amber-400"}`}>
              {mailPoll?.configured ? "Active" : "Not configured"}
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs font-mono mb-4">
            <div><div className="text-muted-foreground uppercase text-[10px]">Mailbox</div><div className="text-primary break-all">{mailPoll?.mailbox || "—"}</div></div>
            <div><div className="text-muted-foreground uppercase text-[10px]">Poll interval</div><div>{mailPoll ? `${mailPoll.interval_seconds}s` : "—"}</div></div>
            <div><div className="text-muted-foreground uppercase text-[10px]">Processed folder</div><div>{mailPoll?.processed_folder || "—"}</div></div>
            <div><div className="text-muted-foreground uppercase text-[10px]">Tickets from email</div><div>{mailPoll?.tickets_from_email ?? "—"}</div></div>
          </div>
          <Button data-testid="mail-poll-run" onClick={runPoll} disabled={polling || !mailPoll?.configured} className="rounded-sm font-mono text-xs uppercase tracking-widest">
            {polling ? "Polling…" : "Poll now"}
          </Button>
          <p className="text-[11px] text-muted-foreground mt-2">Requires the <span className="font-mono text-primary">Mail.ReadWrite</span> application permission (admin consent) on your Entra app, ideally scoped to this mailbox via an Application Access Policy.</p>

          <div className="mt-4 pt-4 border-t border-border/60">
            <div className={`flex items-center justify-between gap-3 rounded-sm border px-3 py-2.5 ${mailPoll?.enabled === false ? "border-amber-500/50 bg-amber-500/10" : "border-emerald-500/40 bg-emerald-500/10"}`} data-testid="mail-poll-toggle-row">
              <div>
                <div className="text-sm font-semibold">{mailPoll?.enabled === false ? "Polling paused" : "Polling active"}</div>
                <div className="text-[11px] text-muted-foreground">{mailPoll?.enabled === false ? "Inbound emails are NOT being turned into tickets." : "Inbound emails are polled and converted into tickets."}</div>
              </div>
              <Button data-testid="mail-poll-toggle-btn" onClick={() => togglePolling(mailPoll?.enabled === false)} variant="outline"
                className={`rounded-sm font-mono text-xs uppercase tracking-widest ${mailPoll?.enabled === false ? "border-emerald-500/50 text-emerald-600 dark:text-emerald-400" : "border-amber-500/50 text-amber-600 dark:text-amber-400"}`}>
                {mailPoll?.enabled === false ? "Resume polling" : "Pause polling"}
              </Button>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-border/60">
            <div className="text-[10px] font-mono uppercase text-muted-foreground mb-2">Mailbox settings</div>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px_auto] gap-3 items-end">
              <div>
                <label className="text-[10px] font-mono uppercase text-muted-foreground">Mailbox to poll &amp; reply from</label>
                <Input data-testid="mail-poll-mailbox-input" value={mbForm.mailbox} onChange={(e) => setMbForm({ ...mbForm, mailbox: e.target.value })} placeholder="helpdesk@procainconsulting.com" className="mt-1 rounded-sm font-mono text-sm" />
              </div>
              <div>
                <label className="text-[10px] font-mono uppercase text-muted-foreground">Interval (sec)</label>
                <Input data-testid="mail-poll-interval-input" type="number" min="15" value={mbForm.interval_seconds} onChange={(e) => setMbForm({ ...mbForm, interval_seconds: e.target.value })} className="mt-1 rounded-sm font-mono text-sm" />
              </div>
              <Button data-testid="mail-poll-save" onClick={saveMailPoll} disabled={savingMb} className="rounded-sm font-mono text-xs uppercase tracking-widest h-10">
                {savingMb ? "Saving…" : "Save"}
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-2">Changes take effect within one polling cycle — no redeploy needed.</p>
          </div>
          <div className="mt-4 pt-4 border-t border-border/60">
            <div className="text-[10px] font-mono uppercase text-muted-foreground mb-1">Maintenance</div>
            <p className="text-[11px] text-muted-foreground mb-2">Bulk-remove mail-loop tickets — bounces (Undeliverable/NDR), auto-replies and out-of-office. Safe to run anytime; it never touches real tickets.</p>
            <Button data-testid="purge-bounces-btn" onClick={purgeBounces} disabled={purging} variant="outline" className="rounded-sm font-mono text-xs uppercase tracking-widest border-red-500/40 text-red-700 dark:text-red-400">
              <Broom size={14} className="mr-2" />{purging ? "Cleaning…" : "Clean up bounce / loop tickets"}
            </Button>
          </div>
        </Card>
      )}

      {isAdmin && alertCfg && (
        <Card className="p-6 border-border rounded-sm mb-6" data-testid="alert-correlation-card">
          <div className="flex items-center gap-2 mb-4">
            <Broadcast size={16} className="text-primary" />
            <span className="text-[11px] font-mono uppercase tracking-widest">Alert Correlation</span>
            <span className={`ml-2 text-[10px] font-mono px-2 py-0.5 border rounded-sm ${alertCfg.enabled ? "border-emerald-500/50 text-emerald-700 dark:text-emerald-400" : "border-amber-500/50 text-amber-700 dark:text-amber-400"}`}>
              {alertCfg.enabled ? "Enabled" : "Disabled"}
            </span>
          </div>
          <p className="text-[11px] text-muted-foreground mb-4 max-w-2xl">
            Tune how inbound monitoring emails are de-duplicated. Emails carrying the same alert token within the window are merged into one ticket as occurrences instead of creating duplicates; a matching recovery keyword auto-resolves the ticket.
          </p>
          <div className="flex items-center justify-between gap-3 rounded-sm border border-border/60 px-3 py-2.5 mb-4">
            <div>
              <div className="text-sm font-semibold">Enable alert correlation &amp; auto-resolve</div>
              <div className="text-[11px] text-muted-foreground">When off, every alert email creates its own ticket.</div>
            </div>
            <Switch data-testid="alert-enabled-switch" checked={!!alertCfg.enabled} onCheckedChange={(v) => setAlertCfg({ ...alertCfg, enabled: v })} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <div>
              <Label className="text-[10px] font-mono uppercase text-muted-foreground">Alert tag / token</Label>
              <Input data-testid="alert-tag-input" value={alertCfg.tag_name} onChange={(e) => setAlertCfg({ ...alertCfg, tag_name: e.target.value })} placeholder="ALERT" className="mt-1 rounded-sm font-mono text-sm" />
            </div>
            <div>
              <Label className="text-[10px] font-mono uppercase text-muted-foreground">ID label</Label>
              <Input data-testid="alert-id-label-input" value={alertCfg.id_label} onChange={(e) => setAlertCfg({ ...alertCfg, id_label: e.target.value })} placeholder="Alert ID" className="mt-1 rounded-sm font-mono text-sm" />
            </div>
            <div>
              <Label className="text-[10px] font-mono uppercase text-muted-foreground">Correlation window (hours)</Label>
              <Input data-testid="alert-window-input" type="number" min="1" value={alertCfg.window_hours} onChange={(e) => setAlertCfg({ ...alertCfg, window_hours: e.target.value })} className="mt-1 rounded-sm font-mono text-sm" />
            </div>
          </div>
          <div className="mb-4">
            <Label className="text-[10px] font-mono uppercase text-muted-foreground">Recovery keywords (comma-separated)</Label>
            <Textarea data-testid="alert-recovery-keywords" value={alertCfg.recovery_keywords} onChange={(e) => setAlertCfg({ ...alertCfg, recovery_keywords: e.target.value })} placeholder="recovered, cleared, back to normal" className="mt-1 rounded-sm text-sm min-h-[72px]" />
            <p className="text-[11px] text-muted-foreground mt-1">A recovery email matching any of these auto-resolves the correlated ticket.</p>
          </div>
          <Button data-testid="alert-config-save" onClick={saveAlertCfg} disabled={savingAlert} className="rounded-sm font-mono text-xs uppercase tracking-widest">
            {savingAlert ? "Saving…" : "Save alert settings"}
          </Button>

          <div className="mt-6 pt-5 border-t border-border/60" data-testid="alert-preview-box">
            <div className="text-[10px] font-mono uppercase text-muted-foreground mb-1">Test this alert</div>
            <p className="text-[11px] text-muted-foreground mb-3">Paste a sample subject &amp; body to see whether it would correlate or auto-resolve using the settings above (uses your unsaved edits too).</p>
            <div className="grid grid-cols-1 gap-3">
              <div>
                <Label className="text-[10px] font-mono uppercase text-muted-foreground">Sample subject</Label>
                <Input data-testid="alert-test-subject" value={alertTest.subject} onChange={(e) => setAlertTest({ ...alertTest, subject: e.target.value })} className="mt-1 rounded-sm text-sm" />
              </div>
              <div>
                <Label className="text-[10px] font-mono uppercase text-muted-foreground">Sample body</Label>
                <Textarea data-testid="alert-test-body" value={alertTest.body} onChange={(e) => setAlertTest({ ...alertTest, body: e.target.value })} className="mt-1 rounded-sm text-sm min-h-[72px]" />
              </div>
            </div>
            <Button data-testid="alert-preview-run" onClick={runAlertPreview} disabled={previewing} variant="outline" className="mt-3 rounded-sm font-mono text-xs uppercase tracking-widest">
              <Broadcast size={14} className="mr-2" />{previewing ? "Checking…" : "Preview outcome"}
            </Button>
            {alertPreview && (
              <div data-testid="alert-preview-result" className={`mt-3 rounded-sm border px-3 py-2.5 text-sm ${alertPreview.would_auto_resolve ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" : alertPreview.would_correlate ? "border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-400" : "border-border bg-muted/30 text-muted-foreground"}`}>
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 border rounded-sm border-current">
                    {alertPreview.would_auto_resolve ? "Auto-resolve" : alertPreview.would_correlate ? "Correlate" : "New ticket"}
                  </span>
                  {alertPreview.alert_key && <span className="text-[11px] font-mono">key: {alertPreview.alert_key}</span>}
                </div>
                {alertPreview.outcome}
              </div>
            )}
          </div>
        </Card>
      )}

      {isAdmin && (
        <Card className="p-6 border-border rounded-sm mb-6" data-testid="ingest-security-card">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck size={16} className="text-primary" />
            <span className="text-[11px] font-mono uppercase tracking-widest">Webhook &amp; Security</span>
            <span className={`ml-2 text-[10px] font-mono px-2 py-0.5 border rounded-sm ${ingest?.configured ? "border-emerald-500/50 text-emerald-700 dark:text-emerald-400" : "border-amber-500/50 text-amber-700 dark:text-amber-400"}`}>
              {ingest?.configured ? "Secured" : "Open — no secret"}
            </span>
          </div>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase text-muted-foreground">Webhook URL (POST from your mail provider)</Label>
              <div className="mt-1 flex gap-2">
                <Input data-testid="ingest-webhook-url" readOnly value={webhookUrl} className="rounded-sm font-mono text-xs" />
                <Button type="button" variant="outline" data-testid="copy-webhook-url" onClick={() => copy(webhookUrl)} className="rounded-sm shrink-0"><Copy size={14} /></Button>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1 font-mono">JSON body: {"{ from_email, subject, body }"}</p>
            </div>
            <div>
              <Label className="text-xs uppercase text-muted-foreground">Shared secret (sent as header <span className="font-mono text-primary">{ingest?.header_name || "X-Ingest-Secret"}</span>)</Label>
              <div className="mt-1 flex gap-2">
                <Input
                  data-testid="ingest-secret-input"
                  value={secret}
                  onChange={(e) => setSecret(e.target.value)}
                  placeholder={ingest?.configured ? `Current: ${ingest.secret_hint} — type to replace` : "No secret set — requests are accepted from anyone"}
                  className="rounded-sm font-mono text-xs"
                />
                <Button type="button" variant="outline" data-testid="gen-secret-btn" onClick={genSecret} className="rounded-sm shrink-0"><ArrowsClockwise size={14} className="mr-1" /> Generate</Button>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">Set a secret so only your Power Automate / Graph flow can create tickets. Leave blank &amp; save to disable. The in-app simulator below always works while you're logged in.</p>
            </div>
            <Button data-testid="save-secret-btn" onClick={saveSecret} disabled={savingSecret} className="rounded-sm font-mono text-xs uppercase tracking-widest">
              {savingSecret ? "Saving…" : "Save secret"}
            </Button>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 border-border rounded-sm">
          <div className="flex items-center gap-2 mb-4">
            <EnvelopeSimple size={16} className="text-primary" />
            <span className="text-[11px] font-mono uppercase tracking-widest">Simulated Message</span>
          </div>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase text-muted-foreground">From</Label>
              <Input data-testid="email-from" className="mt-1 rounded-sm font-mono" value={form.from_email} onChange={(e)=>setForm({...form, from_email: e.target.value})} />
            </div>
            <div>
              <Label className="text-xs uppercase text-muted-foreground">Subject</Label>
              <Input data-testid="email-subject" className="mt-1 rounded-sm" value={form.subject} onChange={(e)=>setForm({...form, subject: e.target.value})} />
            </div>
            <div>
              <Label className="text-xs uppercase text-muted-foreground">Body</Label>
              <Textarea data-testid="email-body" className="mt-1 rounded-sm min-h-[180px]" value={form.body} onChange={(e)=>setForm({...form, body: e.target.value})} />
            </div>
            <Button data-testid="send-email-btn" onClick={send} disabled={saving} className="w-full rounded-sm font-mono text-xs uppercase tracking-widest">
              {saving ? "Sending…" : "Deliver to Helpdesk"}
            </Button>
          </div>
        </Card>
        <Card className="p-6 border-border rounded-sm">
          <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-3">Result</div>
          {created ? (
            <div className="space-y-3">
              <div className="text-primary font-mono text-2xl">{created.number}</div>
              <div className="font-display text-lg">{created.title}</div>
              <div className="text-sm text-muted-foreground">{created.description}</div>
              <Link data-testid="open-created-ticket" to={`/incidents/${created.id}`} className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-primary hover:underline">
                Open ticket <ArrowRight size={12} />
              </Link>
            </div>
          ) : (
            <div className="text-muted-foreground text-sm">No email delivered yet.</div>
          )}
        </Card>
      </div>
    </div>
  );
}
